import { useEffect, useState, type FormEvent } from 'react';
import { Check } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { Field } from '../components/ui';
import { formatUsd, localeUI } from '../lib/format';

type Company = {
  id: string;
  name: string;
  slug: string;
  status: string;
  users: number;
  claims: number;
  invoicesIssued: number;
  feeBilledCents: number;
  moneyReceivedCents: number;
  lastActivity: string | null;
};

export function PlatformDashboard() {
  const { me, loading, logout } = useAuth();
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [feePct, setFeePct] = useState('13');
  const [mName, setMName] = useState('');
  const [mEmail, setMEmail] = useState('');
  const [mPass, setMPass] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);

  async function loadCompanies() {
    try {
      setCompanies((await api.get<{ companies: Company[] }>('/api/platform/companies')).companies);
    } catch {
      /* no-op */
    }
  }
  useEffect(() => {
    if (me?.kind === 'platform') loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  if (loading) return <div className="page-empty">{t('common.loading')}</div>;
  if (me?.kind !== 'platform') {
    return (
      <div className="auth-wrap">
        <div className="auth-card">
          <div className="auth-title">{t('plat.notAuthorized')}</div>
          <div className="auth-sub">{t('auth.platformSub')}</div>
          <a className="btn" href="/platform/login">{t('plat.goLogin')}</a>
        </div>
      </div>
    );
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const r = await api.post<{ companyId: string }>('/api/platform/companies', {
        name,
        slug,
        defaultFeeRateBps: Math.round(parseFloat(feePct) * 100),
        master: { name: mName, email: mEmail, password: mPass },
      });
      setResult(`Tenant creado (companyId ${r.companyId}). El master entra en /login con el slug "${slug}".`);
      setName(''); setSlug(''); setMName(''); setMEmail(''); setMPass('');
      await loadCompanies();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409 ? 'El slug o email ya existen.' : 'Error al crear el tenant.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ maxWidth: 720, margin: '40px auto', padding: '0 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <h1 style={{ fontSize: 'var(--fs-lg)', margin: 0 }}>{t('plat.console')}</h1>
        <button className="btn ghost sm" onClick={() => logout()}>{t('nav.logout')}</button>
      </div>
      <div className="card">
        <h2 className="card-title">{t('plat.createTenant')}</h2>
        <form onSubmit={submit}>
          <div className="grid cols-3">
            <Field label={t('plat.name')} value={name} onChange={setName} placeholder="Proactive" />
            <Field label={t('plat.slug')} value={slug} onChange={setSlug} placeholder="proactive" />
            <Field label={t('plat.fee')} value={feePct} onChange={setFeePct} />
          </div>
          <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '10px 0 14px' }} />
          <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 8 }}>{t('plat.firstUser')}</div>
          <div className="grid cols-3">
            <Field label={t('plat.name')} value={mName} onChange={setMName} />
            <Field label={t('auth.email')} type="email" value={mEmail} onChange={setMEmail} />
            <Field label={t('auth.password')} type="password" value={mPass} onChange={setMPass} />
          </div>
          {error && <div className="error">{error}</div>}
          {result && <div style={{ color: 'var(--green)', fontSize: 'var(--fs-sm)', marginBottom: 10 }}><Check size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {result}</div>}
          <button className="btn" disabled={busy}>{busy ? '…' : t('plat.createBtn')}</button>
        </form>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <h2 className="card-title">{t('plat.tenants')}</h2>
        {companies.length === 0 ? (
          <div className="muted">{t('plat.noTenants')}</div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>{t('plat.colTenant')}</th>
                <th className="num">{t('plat.colUsers')}</th>
                <th className="num">{t('plat.colClaims')}</th>
                <th className="num">{t('plat.colInvoices')}</th>
                <th className="num">{t('plat.colFeeBilled')}</th>
                <th className="num">{t('plat.colMoneyReceived')}</th>
                <th>{t('plat.colLastActivity')}</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((co) => (
                <tr key={co.id}>
                  <td>
                    <strong>{co.name}</strong>
                    <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{co.slug}</div>
                  </td>
                  <td className="num">{co.users}</td>
                  <td className="num">{co.claims}</td>
                  <td className="num">{co.invoicesIssued}</td>
                  <td className="num" style={{ fontFamily: 'var(--font-mono)' }}>{formatUsd(co.feeBilledCents)}</td>
                  <td className="num" style={{ fontFamily: 'var(--font-mono)' }}>{formatUsd(co.moneyReceivedCents)}</td>
                  <td className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                    {/* en-US fijo: la empresa opera en EE.UU. y las fechas se leen mes/día/año aunque la UI esté en español */}
                    {co.lastActivity ? new Date(co.lastActivity).toLocaleDateString(localeUI()) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
